/**
 * How many of a message's own images load themselves when it is opened.
 *
 * An embedded part is not a tracker. The message already carries it, so
 * fetching it tells the sender nothing, and opening the message is the whole
 * statement of intent — a signature logo that needs a button pressed is a bug.
 *
 * The cost is the signer, not privacy. Every blob fetch carries a freshly
 * signed authorization, and with a phone signer that is one approval per image,
 * so a ten-image newsletter opened by accident would be ten prompts. A key held
 * in the tab pays nothing and takes them all; a remote one takes a handful and
 * leaves the rest behind the "Load N images" button, where the reader can ask
 * for them and know what they are asking for.
 */

import type { BlobRef } from "../nostr/seal";

/**
 * The bytes of a remote image, fetched by the page and never by the tag.
 *
 * The CSP allows `img-src 'self' blob: data:` and nothing else, so a remote
 * `src` cannot load — which is the tracker rule enforced a second time by the
 * browser. A permitted sender's images come through here instead: one fetch
 * with no referrer, the bytes handed to the page as an object URL. The sender
 * learns nothing this account did not already tell them by asking.
 *
 * Null for anything that is not an image: a sender who named a tracker where a
 * photo belongs gets nothing, and a failure is quiet — the placeholder already
 * says the image is blocked.
 */
export async function fetchRemoteImage(
  url: string,
  maxBytes = 8 * 1024 * 1024,
): Promise<{ bytes: Uint8Array; type: string } | null> {
  try {
    const response = await fetch(url, { referrerPolicy: "no-referrer", mode: "cors" });
    if (!response.ok) return null;
    const type = (response.headers.get("content-type") ?? "").toLowerCase();
    if (!type.startsWith("image/")) return null;
    const buffer = await response.arrayBuffer();
    if (buffer.byteLength > maxBytes) return null;
    return { bytes: new Uint8Array(buffer), type };
  } catch {
    return null;
  }
}

/** Enough for a signature and a header mark, short of a newsletter. */
export const REMOTE_AUTO_LOAD = 3;

/**
 * Split the parts a message carries into the ones to fetch on open and the ones
 * to leave behind the button.
 */
export function autoLoad<T>(refs: readonly T[], remote: boolean): { load: T[]; defer: T[] } {
  const limit = remote ? REMOTE_AUTO_LOAD : refs.length;
  return { load: refs.slice(0, limit), defer: refs.slice(limit) };
}

/**
 * Which stored body must be fetched before the message can be drawn.
 *
 * HTML wins whenever it is not already in hand, even when a plain copy came
 * inline — which is the usual shape of a message whose body was too big to
 * seal. The plain copy is a flattened alternative with the sender's images
 * stripped out of it, so rendering it and stopping loses every image the
 * message carries. Nothing is fetched when the preferred body is already held.
 */
export function bodyToFetch(item: {
  html?: string;
  text?: string;
  html_ref?: BlobRef;
  text_ref?: BlobRef;
}): BlobRef | null {
  const html = item.html?.trim() ?? "";
  const text = item.text?.trim() ?? "";
  if (html) return null;
  if (item.html_ref) return item.html_ref;
  if (!text && item.text_ref) return item.text_ref;
  return null;
}

/**
 * The parts of a message that are meant to be looked at.
 *
 * `embedded` are the `cid:` parts the body places itself. `attached` are image
 * files: they keep their download pill, because the file is still the point,
 * and they are drawn under the body as well — a photo nobody can see until
 * they download it is a mail client failing at what the message was sent for.
 */
export function imageParts(refs: readonly BlobRef[]): {
  embedded: BlobRef[];
  attached: BlobRef[];
} {
  return {
    embedded: refs.filter((ref) => ref.inline && ref.content_id),
    attached: refs.filter(
      (ref) => !ref.inline && (ref.content_type ?? "").toLowerCase().startsWith("image/"),
    ),
  };
}
